<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-arrow">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{ slug ? "" : "Registrar Marca" }}
                    <strong v-if="slug" class="text-theme-primary">
                        {{ itemComponent.name }}
                    </strong>
                </h1>
                <div v-if="slug" class="flex">
                    <strong class="text-sm text-theme-muted">
                        Slug {{ itemComponent.slug }}
                    </strong>
                </div>
            </div>
            <div
                v-if="slug"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small> 📆💾 {{ itemComponent.createdAt | date }} </small>
                <small> 📆✏ {{ itemComponent.updatedAt | date }} </small>
            </div>
        </div>

        <div class="my-4">
            <custom-navbar
                id="tabs-brands"
                v-model="activeTab"
                center-collapsed
                not-line
            >
                <template #left>
                    <custom-navbar-item id="home">
                        <router-link
                            class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                            to="/app/brands"
                        >
                            <span>🏠</span>
                        </router-link>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="general"
                        :active="['general', 'home'].includes(activeTab)"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> General </span>
                            <span>&nbsp;⚙️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="
                            $ability.hasPermission('brand:Get') &&
                            $enabledEcommerceOptions &&
                            !isCreation
                        "
                        id="seo"
                        :active="activeTab === 'seo'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Contenido&nbsp;
                            </span>
                            <span>&nbsp;📒</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug && $ability.hasPermission('brand:Delete')"
                        id="advanced"
                        :active="activeTab === 'advanced'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Avanzado&nbsp;
                            </span>
                            <span>&nbsp;🤖</span>
                        </div>
                    </custom-navbar-item>
                </template>
                <template #right>
                    <div class="hidden md:block">
                        <custom-switch
                            v-if="
                                $ability.hasPermission('brand:Update') &&
                                !isCreation
                            "
                            v-model="enabledUpdate"
                        >
                            <span class="text-theme-inverse">🖊</span>
                        </custom-switch>
                    </div>
                </template>
            </custom-navbar>
        </div>

        <div class="flex justify-center">
            <div class="block md:hidden my-3">
                <custom-switch
                    v-if="$ability.hasPermission('brand:Update') && !isCreation"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div v-show="['general', 'home'].includes(activeTab)" id="generalTab">
            <div
                v-if="slug && $enabledEcommerceOptions"
                class="flex flex-wrap px-2"
            >
                <div class="w-full flex justify-start items-center">
                    <active-selector
                        v-model="itemComponent.active"
                        :enabled="enabledUpdate"
                        :public-url="publicBrandUrl"
                        :public-label="publicBrandLabel"
                        on-label="Publicado"
                        off-label="Oculto"
                    />
                </div>
            </div>

            <div class="flex flex-wrap mt-3">
                <div
                    class="w-full mt-5 px-2"
                    :class="isCreation ? 'w-full md:w-1/2' : 'w-full'"
                >
                    <custom-input
                        v-model="itemComponent.name"
                        label="Nombre"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidName & introValidators.name
                                ? 'Nombre de marca invalida'
                                : ''
                        "
                        @blur="blurName"
                    />
                </div>
                <div v-if="isCreation" class="w-full mt-5 md:w-1/2 px-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug & introValidators.slug
                                ? 'Slug de marca invalido'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    >
                        <template v-if="slug" #icon> 🧾 </template>
                    </custom-input>
                </div>

                <div class="w-full mt-2 px-2">
                    <custom-input
                        v-model="itemComponent.summary"
                        label="Descripción"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSummary & introValidators.summary
                                ? 'máximo 255'
                                : ''
                        "
                        @blur="introValidators.summary = true"
                    />
                </div>

                <div
                    class="flex flex-col items-start md:flex-row w-full gap-4 mt-8 px-2"
                >
                    <div class="w-full md:w-1/2">
                        <fragment v-if="$enabledEcommerceOptions">
                            <images-manage
                                :id="slug"
                                v-model="images"
                                type="marca"
                                :enabled="enabledUpdate"
                                :default-alt="'Imagen de ' + itemComponent.name"
                                default-size="squareImage"
                            />
                        </fragment>
                    </div>
                </div>

                <div class="w-full mt-8">
                    <div
                        v-if="enabledUpdate"
                        class="w-full flex flex-col sm:flex-row justify-center"
                    >
                        <app-button
                            v-if="!slug"
                            class="w-full md:w-auto"
                            size="large"
                            color="success"
                            border
                            :loading="isLoading"
                            @click="createBrandComponent(false)"
                        >
                            Crear marca
                        </app-button>
                        <app-button
                            v-if="!slug"
                            class="w-full md:w-auto"
                            size="large"
                            color="success"
                            :loading="isLoading"
                            @click="createBrandComponent(true)"
                        >
                            Crear y publicar
                        </app-button>
                        <app-button v-else @click="updateBrandComponent">
                            Actualizar marca
                        </app-button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'seo'" id="seoTab" class="space-y-6">
            <div id="seo-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl font-semibold my-4">
                        Contenido de la marca
                    </h2>

                    <span>
                        Este contenido es el que se va a mostrar en la página de
                        la marca, es importante que esté bien redactado y sea
                        atractivo para los usuarios, ya que esto va a influir en
                        la decisión de compra de los usuarios.
                    </span>
                </div>
            </div>

            <div class="bg-theme-card rounded-2xl shadow-sm p-5 mt-5">
                <h2
                    class="text-theme-primary text-xl font-semibold mb-4 flex items-center gap-2"
                >
                    Contenido de la marca
                </h2>
                <div v-if="$enabledEcommerceOptions">
                    <markdown-editor
                        v-model="itemComponent.content"
                        class="mt-2"
                        :enabled="enabledUpdate"
                    />
                </div>
            </div>
            <div
                v-if="$enabledEcommerceOptions"
                class="bg-theme-card rounded-2xl shadow-sm p-5"
            >
                <h2
                    class="text-theme-primary text-xl font-semibold mb-4 flex items-center gap-2"
                >
                    Palabras clave
                </h2>
                <keywords-manage
                    v-model="itemComponent.keywords"
                    :base="$data.$keywordsBase"
                    :enabled="enabledUpdate"
                />
            </div>
            <div v-if="enabledUpdate" class="w-full flex justify-center">
                <app-button
                    :loading="isLoadingContent"
                    @click="updateBrandContentComponent"
                >
                    Actualizar Contenido
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'advanced'" id="advancedTab">
            <div class="flex flex-col mt-4">
                <h2 class="text-theme-primary text-2xl font-semibold">
                    Avanzado
                </h2>
                <span class="text-theme-muted mt-1">
                    Puedes eliminar la marca aquí, ten mucho cuidado.
                </span>
            </div>
            <div class="flex justify-around mt-5">
                <app-button
                    v-if="$ability.hasPermission('brand:Delete') && slug"
                    danger
                    @click="deleteBrandComponent"
                >
                    Eliminar marca
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { slugRegex, summaryRegex, titleRegex } from "@/common/lib/regex";
import { slugify } from "@/common/lib/transformers";
import { stripUrlProtocol } from "@/common/lib/url";

import ActiveSelector from "@/components/utils/ActiveSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import KeywordsManage from "@/components/utils/KeywordsManage.vue";
import MarkdownEditor from "@/components/utils/MarkdownEditor.vue";

export default {
    name: "CreateUpdateBrand",
    components: {
        ImagesManage,
        ActiveSelector,
        MarkdownEditor,
        KeywordsManage,
        CustomInput
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        slug: "",
        name: "",
        images: [],
        isLoading: false,
        isCreation: true,
        isLoadingContent: false,
        enabledUpdate: false,
        activeTab: "general",
        $tabsWhitelist: ["general", "seo", "advanced"],
        introValidators: { slug: false, name: false, summary: false },
        itemComponent: {
            name: "",
            content: "",
            slug: "",
            summary: "",
            keywords: [],
            images: [],
            active: false
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        },
        isValidName() {
            return titleRegex.test(this.itemComponent.name);
        },
        isValidSummary() {
            return summaryRegex.test(this.itemComponent.summary);
        },
        publicBrandUrl() {
            if (!this.itemComponent?.slug || !this.$publicUrl) {
                return "";
            }
            return `${this.$publicUrl}/brands/${this.itemComponent.slug}`;
        },
        publicBrandLabel() {
            return stripUrlProtocol(this.publicBrandUrl);
        }
    },
    watch: {
        "$route.params.slug"() {
            this.resetData();
            this.initComponent();
        },
        "$route.params.slugClone"() {
            this.resetData();
            this.initComponent();
        }
    },
    async mounted() {
        await this.initComponent();
    },
    methods: {
        ...mapActions("brands", [
            "getBrandBySlug",
            "updateBrand",
            "createBrand",
            "deleteBrand"
        ]),
        resetData() {
            this.slug = "";
            this.name = "";
            this.images = [];
            this.isLoading = false;
            this.isLoadingContent = false;
            this.enabledUpdate = false;
            this.activeTab = "general";
            this.introValidators = { slug: false, name: false, summary: false };
            this.itemComponent = {
                name: "",
                content: "",
                slug: "",
                summary: "",
                keywords: [],
                images: [],
                active: false
            };
        },
        async initComponent() {
            const verifyPagePermission =
                this.$ability.verifyPagePermission.bind(this);
            this.isCreation = true;
            if ("slug" in this.$route.params) {
                this.isCreation = false;
                const slug = this.$route.params.slug;
                this.slug = slug;
                await this.getBaseItem(slug);
            } else if ("slugClone" in this.$route.params) {
                if (
                    !(await verifyPagePermission("brand:Create", "/app/brands"))
                )
                    return;
                this.enabledUpdate = true;
                const slugTmp = this.$route.params.slugClone;
                await this.getBaseItem(slugTmp);
                this.slug = "";
                this.itemComponent = {
                    ...this.itemComponent,
                    name: `${this.itemComponent.name} CLONE`,
                    slug: `${this.itemComponent.slug}-clone`,
                    active: false
                };
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Estas clonando una marca",
                    icon: "info",
                    showCloseButton: true
                });
            } else {
                if (
                    !(await verifyPagePermission("brand:Create", "/app/brands"))
                )
                    return;
                this.enabledUpdate = true;
            }
        },
        async getBaseItem(slug) {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getBrandBySlug({ slug });
                this.name = this.itemComponent.name;
                this.images = this.itemComponent.images;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/brands");
                }
            }
        },
        async updateBrandComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateBrand(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createBrandComponent(publish = false) {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createBrand({
                    ...this.getDataSend(),
                    ...this.getContentDataSend(),
                    active: publish
                });
                this.$loading.hide();
                this.itemComponent = { ...data, ...this.itemComponent };
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Slug: ${data.slug}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista de marcas",
                    cancelButtonText: "Registrar marca"
                });
                if (dialogResult.isConfirmed) {
                    this.slug = data.slug;
                    this.isCreation = false;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/brands/detail/${this.slug}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/brands");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        slug: false,
                        name: false,
                        summary: false
                    };
                    this.itemComponent = {
                        name: "",
                        content: "",
                        slug: "",
                        summary: "",
                        keywords: [],
                        images: [],
                        active: false
                    };
                    this.images = [];
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async setSlug() {
            if (this.isCreation && this.isValidName) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "¿Deseas cambiar el slug?",
                    text: "Ten en cuenta que el slug solo puede definirse o modificarse durante la creación. Una vez guardado, no podrá ser cambiado posteriormente.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Sí, cambiar",
                    cancelButtonText: "Cancelar"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.name);
                }
            }
        },
        blurName() {
            this.introValidators.name = true;
            this.setSlug();
        },
        validateData() {
            const toComplete = [];

            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidSummary) {
                toComplete.push("Descripción");
            }

            if (!this.isValidName) {
                toComplete.push("Nombre");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Marca invalida",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }

            return true;
        },
        getDataSend() {
            return {
                slug: this.itemComponent.slug,
                name: this.itemComponent.name,
                summary: this.itemComponent.summary,
                active: this.itemComponent.active,
                images: this.images.map((item) => ({
                    id: item.id,
                    sequence: item.sequence.sequence
                }))
            };
        },
        getContentDataSend() {
            return {
                slug: this.itemComponent.slug,
                content: this.itemComponent.content,
                keywords: this.itemComponent.keywords
            };
        },
        async deleteBrandComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "¿Seguro deseas eliminar la marca?",
                text: "Esta acción eliminará la marca. Ten mucho cuidado.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#EA5455",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Sí, eliminar",
                cancelButtonText: "Cancelar"
            });
            if (!dialogResult.isConfirmed) return;
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.deleteBrand({ slug: this.itemComponent.slug });
                this.$toast.success({
                    title: `Marca "${this.itemComponent.name}" eliminada`
                });
                this.$router.push("/app/brands");
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateBrandContentComponent() {
            this.isLoadingContent = true;
            this.$loading.show();
            try {
                await this.updateBrand(this.getContentDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoadingContent = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoadingContent = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
